import { Table, TableBody, TableContainer } from "@crayon/design-system-react";
import {
  GetCustomerSyncResultsResponse,
  SubscriptionSyncResult,
  SyncOutcome,
  Target,
} from "api/client.generated";
import SubscriptionsSyncDetailsLayout from "components/layout/Sync/SubscriptionsSyncDetailsLayout";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableBodySyncOutcomeCell from "components/primitives/TableBodySyncOutcomeCell";
import TableHeadSortable, { SortableColumn } from "components/primitives/TableHeadSortable";
import TableRowCollapsible from "components/primitives/TableRowCollapsible";
import { useMemo, useState } from "react";
import SortDirection from "types/sort-direction";
import compareModelsFn from "utils/sort-fn";
import {
  getTargetAgreementLabel,
  getTargetCustomerLabel,
} from "utils/target-customer-agreement-labels";

interface CustomerSyncResultTableModel {
  customerMappingId: string;
  sourceCustomerName: string;
  sourceCustomerAgreementName: string;
  targetCustomerName: string;
  targetCustomerAgreementName: string;
  invoiceNumber: string;
  syncOutcome: SyncOutcome;
  errorMessage: string | undefined;
  subscriptionSyncResults: SubscriptionSyncResult[];
}

interface CustomersSyncDetailsTableProps {
  customerDigest: GetCustomerSyncResultsResponse | undefined;
  isCustomerDigestFetching: boolean;
  target: Target;
}

const CustomersSyncDetailsTable = (props: CustomersSyncDetailsTableProps) => {
  const { customerDigest, isCustomerDigestFetching, target } = props;
  const [sortBy, setSortBy] = useState<keyof CustomerSyncResultTableModel>("sourceCustomerName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const syncResults = useMemo<CustomerSyncResultTableModel[]>(
    () =>
      customerDigest?.digest.results
        .map((x) => ({
          customerMappingId: x.customerMappingId,
          sourceCustomerName: x.sourceCustomer?.name ?? "",
          sourceCustomerAgreementName: x.sourceCustomer?.agreement?.name ?? "",
          targetCustomerName: x.targetCustomer?.name ?? "",
          targetCustomerAgreementName: x.targetCustomer?.agreements?.at(0)?.name ?? "",
          invoiceNumber: x.sourceCustomer?.invoiceNumber ?? "",
          syncOutcome: x.outcome,
          errorMessage: x.errorMessage,
          subscriptionSyncResults: x.subscriptionSyncResults,
        }))
        .sort((a, b) => compareModelsFn(a, b, sortBy, sortDirection)) ?? [],
    [customerDigest, sortBy, sortDirection],
  );

  const columnOptions = useMemo<SortableColumn<CustomerSyncResultTableModel>[]>(
    () => [
      { label: "Source Customer", field: "sourceCustomerName" },
      { label: "Source Agreement", field: "sourceCustomerAgreementName" },
      { label: getTargetCustomerLabel(target), field: "targetCustomerName" },
      { label: getTargetAgreementLabel(target), field: "targetCustomerAgreementName" },
      { label: "Invoice", field: "invoiceNumber" },
      { label: "Status", field: "syncOutcome" },
    ],
    [target],
  );

  const onSortRequest = (
    newSortField: keyof CustomerSyncResultTableModel,
    newSortDirection: SortDirection,
  ) => {
    setSortBy(newSortField);
    setSortDirection(newSortDirection);
  };

  const renderCell = (label: string | undefined) =>
    label ? <TableBodyCell label={label} /> : <TableBodyCell label="Missing" fontItalic />;

  const collapsibleContent = (digest: CustomerSyncResultTableModel) => {
    if (!digest.errorMessage && !digest.subscriptionSyncResults?.length) return undefined;

    return (
      <SubscriptionsSyncDetailsLayout
        subscriptionSyncResults={digest.subscriptionSyncResults}
        errorMessage={digest.errorMessage}
      />
    );
  };

  const DataTableBody = (
    <TableBody>
      {syncResults.map((digest) => (
        <TableRowCollapsible
          key={digest.customerMappingId}
          columnCount={columnOptions.length}
          oneRow={false}
          collapsibleContent={collapsibleContent(digest)}
        >
          {renderCell(digest.sourceCustomerName)}
          {renderCell(digest.sourceCustomerAgreementName)}
          {renderCell(digest.targetCustomerName)}
          {renderCell(digest.targetCustomerAgreementName)}
          <TableBodyCell label={digest.invoiceNumber ?? ""} />
          <TableBodySyncOutcomeCell syncOutcome={digest.syncOutcome} />
        </TableRowCollapsible>
      ))}
    </TableBody>
  );

  return (
    <TableContainer>
      <Table testId="Customer Sync Details">
        <TableHeadSortable
          columns={columnOptions}
          onSortRequest={onSortRequest}
          sortBy={sortBy}
          sortDirection={sortDirection}
        />

        {isCustomerDigestFetching ? (
          <TableBodySkeleton colsCount={columnOptions.length} rowsCount={3} />
        ) : (
          DataTableBody
        )}
      </Table>
    </TableContainer>
  );
};

export default CustomersSyncDetailsTable;
